<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import dealReportsAPI from 'dashboard/api/dealReports';
import { useCommercialGoalsStore } from 'dashboard/stores/commercialGoals';
import { useCommercialGoalFormat } from 'dashboard/components-next/CommercialGoals/useCommercialGoalFormat';
import GoalProgressBar from 'dashboard/components-next/CommercialGoals/GoalProgressBar.vue';
import { formatCurrencyBRL, formatDuration } from './commercialFormatters';
import { CARD_GOAL_TYPES, findTeamGoal } from './commercialGoalMatchers';

const props = defineProps({
  params: { type: Object, required: true },
});

const { t } = useI18n();
const goalsStore = useCommercialGoalsStore();
const { formatValue, formatPeriod } = useCommercialGoalFormat();
const data = ref(null);
const isLoading = ref(true);
const hasError = ref(false);

const fetchData = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const response = await dealReportsAPI.getSummary(props.params);
    data.value = response.data;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.params, fetchData, { immediate: true, deep: true });

const CARDS = [
  { key: 'revenue', labelKey: 'REVENUE', format: formatCurrencyBRL },
  { key: 'won_count', labelKey: 'WON_COUNT' },
  {
    key: 'average_ticket',
    labelKey: 'AVERAGE_TICKET',
    format: formatCurrencyBRL,
  },
  {
    key: 'conversion_rate',
    labelKey: 'CONVERSION_RATE',
    format: value => `${value}%`,
  },
  {
    key: 'average_purchase_time_seconds',
    labelKey: 'AVERAGE_PURCHASE_TIME',
    format: formatDuration,
  },
  { key: 'created_count', labelKey: 'CREATED_COUNT' },
  { key: 'open_count', labelKey: 'OPEN_COUNT' },
  { key: 'open_value', labelKey: 'OPEN_VALUE', format: formatCurrencyBRL },
  { key: 'lost_count', labelKey: 'LOST_COUNT' },
  { key: 'lost_value', labelKey: 'LOST_VALUE', format: formatCurrencyBRL },
];

const displayValue = card => {
  const raw = data.value?.[card.key];
  if (raw === undefined || raw === null) return '—';
  return card.format ? card.format(raw) : raw;
};

// Meta de equipe vigente do mesmo tipo do cartão (independe dos filtros da tela).
const goalFor = card =>
  findTeamGoal(goalsStore.progress, CARD_GOAL_TYPES[card.key]);

const goalTooltip = goal =>
  t('DASHBOARD.COMERCIAL.GOALS.TOOLTIP', {
    period: formatPeriod(goal),
    current: formatValue(goal.goalType, goal.progress.currentValue),
    target: formatValue(goal.goalType, goal.progress.targetValue),
  });
</script>

<template>
  <section class="border rounded-lg border-n-weak">
    <h2 class="p-3 font-medium text-n-slate-12">
      {{ t('DASHBOARD.COMERCIAL.SECTIONS.SUMMARY') }}
    </h2>
    <p v-if="hasError" class="px-3 pb-3 text-sm text-n-ruby-9">
      {{ t('DASHBOARD.COMERCIAL.LOAD_ERROR') }}
      <button type="button" class="underline" @click="fetchData">
        {{ t('DASHBOARD.COMERCIAL.RETRY') }}
      </button>
    </p>
    <p
      v-else-if="data?.excluded_other_currency_count"
      class="px-3 pb-3 text-sm text-n-amber-11"
    >
      {{
        t('DASHBOARD.COMERCIAL.OTHER_CURRENCY_WARNING', {
          n: data.excluded_other_currency_count,
        })
      }}
    </p>
    <div
      v-if="!hasError"
      class="grid grid-cols-2 gap-3 p-3 pt-0 sm:grid-cols-3 lg:grid-cols-5"
    >
      <div
        v-for="card in CARDS"
        :key="card.key"
        class="p-3 rounded-md bg-n-alpha-1"
      >
        <p class="flex items-center gap-1 mb-1 text-xs text-n-slate-10">
          <span>{{ t(`DASHBOARD.COMERCIAL.CARDS.${card.labelKey}`) }}</span>
          <fluent-icon
            v-tooltip="t(`DASHBOARD.COMERCIAL.TOOLTIPS.${card.labelKey}`)"
            icon="info"
            size="12"
          />
        </p>
        <p class="text-lg font-medium text-n-slate-12">
          {{
            isLoading ? t('DASHBOARD.COMERCIAL.LOADING') : displayValue(card)
          }}
        </p>
        <GoalProgressBar
          v-if="goalFor(card)"
          class="mt-2"
          compact
          :percent="goalFor(card).progress.progressPercent"
          :state="goalFor(card).progress.state"
          :tooltip="goalTooltip(goalFor(card))"
        />
      </div>
    </div>
  </section>
</template>
